import * as Yup from "yup";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { Checkout } from "src/organisms/CheckoutView/CheckoutStepInfo";
import { getCTCustomField } from "src/utils/ct/attributeHelper";
import { isValidPostcode, isValidStreet } from "src/utils/validations";
import { Cart } from "src/types/ctgraphql.d";
import { Address } from "src/molecules/AddressForm/AddressForm";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import getConfig from "next/config";
import { storeConfigs } from "./storeConfig";
import { StoreKey } from "lib/Intl";

type ValidationContext = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  shippingAddress: Address;
  billingAddress?: Address;
};

const { publicRuntimeConfig } = getConfig();
const storeConfig =
  storeConfigs[(publicRuntimeConfig.STORE_KEY as StoreKey) || ""];

const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žȘșȚțŒœŸŁłŽžğåøðšůýæ' .-]+$/;

const userValidationSchema = {
  firstName: Yup.string()
    .trim()
    .test(
      "no-only-spaces-check",
      FORM_ERROR_MESSAGES.required,
      (string) => !!string
    )
    .matches(nameRegex, FORM_ERROR_MESSAGES.invalidName)
    .max(25, FORM_ERROR_MESSAGES.firstNameMax),
  lastName: Yup.string()
    .trim()
    .test(
      "no-only-spaces-check",
      FORM_ERROR_MESSAGES.required,
      (string) => !!string
    )
    .matches(nameRegex, FORM_ERROR_MESSAGES.invalidName)
    .max(25, FORM_ERROR_MESSAGES.lastNameMax),
  email: Yup.string()
    .trim()
    .email(FORM_ERROR_MESSAGES.email)
    .max(50, FORM_ERROR_MESSAGES.emailMax)
    .required(FORM_ERROR_MESSAGES.required),
  phone: Yup.string()
    .trim()
    .test({
      test: (value, { parent }) => {
        if (!value) {
          return true;
        }

        const defaultCountryCode =
          ((parent as ValidationContext)?.shippingAddress
            ?.country as CountryCode) ||
          (storeConfig.countryCode as CountryCode) ||
          "NL";
        const phoneNumber = parsePhoneNumberFromString(
          value,
          defaultCountryCode
        );

        if (!phoneNumber || !phoneNumber.isValid()) {
          return false;
        }

        return true;
      },
      message: FORM_ERROR_MESSAGES.phone,
    })
    .max(20, FORM_ERROR_MESSAGES.phoneMax),
};

const addressValidationSchema = {
  streetName: Yup.string()
    .trim()
    .test("valid-street-check", FORM_ERROR_MESSAGES.streetName, (value) =>
      isValidStreet(value || "")
    )
    .max(50, FORM_ERROR_MESSAGES.streetNameMax)
    .required(FORM_ERROR_MESSAGES.streetNameRequired),
  streetNumber: Yup.string()
    .trim()
    .when("country", {
      is: (country: string) => country !== "US",
      then: Yup.string()
        .max(8, FORM_ERROR_MESSAGES.streetNumberMax)
        .when("country", {
          is: (country: string) => country === "FR",
          then: (schema) =>
            schema
              .matches(
                /^$|[0-9a-zA-Z-_\s]+$/i,
                FORM_ERROR_MESSAGES.streetNumber
              )
              .when(["isStreetNumberNotRequired"], {
                is: (isStreetNumberNotRequired: boolean) =>
                  !isStreetNumberNotRequired,
                then: (schema2) =>
                  schema2.required(FORM_ERROR_MESSAGES.streetNumberRequired),
              }),
          otherwise: (schema) =>
            schema
              .matches(/[0-9a-zA-Z-_\s]+$/i, FORM_ERROR_MESSAGES.streetNumber)
              .required(FORM_ERROR_MESSAGES.streetNumberRequired),
        }),
    }),
  additionalStreetInfo: Yup.string()
    .trim()
    .max(50, FORM_ERROR_MESSAGES.additionalStreetInfoMax),
  postalCode: Yup.string()
    .trim()
    .max(10, FORM_ERROR_MESSAGES.postalCodeMax)
    .required(FORM_ERROR_MESSAGES.postalCodeRequired)
    .test(
      "valid-postcode-check",
      FORM_ERROR_MESSAGES.postalCode,
      (value, { parent }) =>
        isValidPostcode(
          value || "",
          ((parent as Address)?.country as string) || ""
        )
    ),
  city: Yup.string()
    .trim()
    .test(
      "no-only-spaces-check",
      FORM_ERROR_MESSAGES.required,
      (string) => !!string
    )
    .max(50, FORM_ERROR_MESSAGES.cityMax),
  state: Yup.string()
    .trim()
    .when("country", {
      is: "US",
      then: Yup.string().test(
        "no-only-spaces-check",
        FORM_ERROR_MESSAGES.stateRequired,
        (string) => !!string
      ),
    }),
  isStreetNumberNotRequired: Yup.boolean(),
};

const profileValidationSchema = {
  ...userValidationSchema,
  gender: Yup.string()
    .trim()
    .test("no-only-spaces-check", FORM_ERROR_MESSAGES.selectGender, (string) =>
      ["female", "male"].includes(string || "")
    ),
  bithdate: Yup.string(),
  shippingAddress: Yup.object().shape(addressValidationSchema),
};

const checkoutValidationSchema = {
  ...userValidationSchema,
  shippingAddress: Yup.object().shape(addressValidationSchema),
  billingAddress: Yup.object().shape(addressValidationSchema),
};

const getIsCheckoutValid = (checkout: Checkout): boolean =>
  Yup.object().shape(checkoutValidationSchema).isValidSync(checkout);

const getIsAddressValid = (address: Address): boolean =>
  Yup.object().shape(addressValidationSchema).isValidSync(address);

const getIsShippingMethodValid = (cart: Cart | null): boolean => {
  const isPudo = cart?.shippingInfo?.shippingMethod?.key === "pudo-shipping";
  const pudoCustomField =
    (getCTCustomField("pudo", cart?.custom?.customFieldsRaw) as string) || "";
  return (
    !!cart?.shippingInfo?.shippingMethod?.id &&
    (isPudo ? !!pudoCustomField : true)
  );
};

export {
  checkoutValidationSchema,
  profileValidationSchema,
  getIsCheckoutValid,
  getIsAddressValid,
  getIsShippingMethodValid,
};
